import { Injectable } from '@angular/core';
import { SxHttpService } from './sxHttp.service';
import { Observable } from 'rxjs/Observable';
import { Rule } from '../model/interfaces/rule.interface';
import { FullContextStandardFlowSubscribe, FullContextStandardFlow, FullContext } from '../model/interfaces/fullContextModel.interface';
import { FlowDynamicStyle } from '../model/interfaces/flowDynamicStyle.interface';
import { StandardDictionary } from '../model/interfaces/standarddictionary.interface';
import { StandardFileContentModel } from './azureFileManager.service';
import { Marketplace } from '../model/classes/marketplace.class';

export interface ProductFlowConfig {
  flowId: number;
  title:string;
  flowCode: string;
  rules: FlowElement[];
  pages: FlowElementPage[];
  interactionRules: FlowElement[];
  categories: any;
  launchEvents: LaunchEvent[];
  hasReferences: Boolean
  standardDictionaryId: string;
  standardDictionaryName: string;
}

export interface FlowImportRequest{
  systemCode : string;
  businessCode  : string;
  productCode  : string;
  fileName  : string;
  flowCode  : string;
  file  : string;
  documentKind  : string;
  categories: string[];
  standardDictionary: string;
}

export interface ProductFlowDesignerConfig {
  flowId: number;
  flowCode: string;
  standardkey: string;
  source: number;
  rules: any[];
  pages: any[];
  attributeCodes: any[];
  pgCodes: any[];
}

export interface LaunchEvent {
  id: string;
  label: string;
  active: boolean;
  rule: FlowElement;
}

export interface FlowElement {
  value: string;
  name: string;
}

export interface FlowElementPage extends FlowElement {
  pageNumber: number;
  value: string;
  name: string;
}

export interface RuleLevel {
  codInstalacionV5: string;
  codNegocio: string;
  codProducto: string;
  idOpcionNegocio: number;
  idRegla: number;
  codFlow: string;
  standardkey: string;
}

export interface FlowRulePostRequest {
  rule: Rule;
  level: RuleLevel;
}

@Injectable()
export class ProductFlowService {
  private configUrl = 'api/ProductFlow';
  private StandardFlowUrl = 'api/stardardProductFlow';
  private CategoriesUrl = 'api/category';
  private standardDictionary = "api/standarddictionary";

  constructor(private sxHttp: SxHttpService) { }

  // gets all the flows of a product
  getProductFlows(codSistema, codNegocio, codProducto, documentKind): Observable<ProductFlowConfig[]> {
    const url = `${this.configUrl}/GetAll/${codSistema}/${codNegocio}/${codProducto}/${documentKind}`;
    return this.sxHttp.get(url)
      .map(response => response.json().data as ProductFlowConfig)
      .catch(this.handleError);
  }
  getStandardFlows(documentKind, standardKey): Observable<ProductFlowConfig[]> {
    const url = `${this.StandardFlowUrl}/GetAll/${documentKind}/${standardKey}`;
    return this.sxHttp.get(url)
      .map(response => response.json().data as ProductFlowConfig)
      .catch(this.handleError);
  }
  getStandardProductFlows(codSistema, codNegocio, codProducto, documentKind, standardKey): Promise<ProductFlowConfig[]> {
    const url = `${this.StandardFlowUrl}/GetAllForProduct`;
    const req: FullContextStandardFlow = {
      systemCode: codSistema,
      businessCode: codNegocio,
      productCode: codProducto,
      flowCode: null,
      documentKind: documentKind,
      categories: [],
      dictionaryKey: 'standardDictionary',
      standardDictionaryId:null
    };
    return this.sxHttp.post(url, req)
      .toPromise()
      .then(response => { return response.json().data as ProductFlowConfig[] })
      .catch(this.handleError);
  }
  
  getStandardProductFlowsForBinded(codSistema, codNegocio, codProducto, documentKind, standardKey): Observable<ProductFlowConfig[]> {
    const url = `${this.StandardFlowUrl}/GetBindedByContext/${codSistema}/${codNegocio}/${codProducto}/${documentKind}/${standardKey}`;
    return this.sxHttp.get(url)
      .map(response => response.json().data as ProductFlowConfig)
      .catch(this.handleError);
  }

  // this creates a new flow object
  CreateNewFlow(codSistema: string, codNegocio: string, codProduct: string,
    codflow: string, documentKind: string) {
    const url = `${this.configUrl}/CreateNewFlow`;
    const req: FullContext = {
      systemCode: codSistema,
      businessCode: codNegocio,
      productCode: codProduct,
      flowCode: codflow,
      documentKind: documentKind
    };


    return this.sxHttp.post(url, req)
      .toPromise()
      .then(response => response.json().data as ProductFlowConfig)
      .catch(this.handleError);
  }

  CreateNewStandardFlow(codFlow: string, documentKind: string, categories: any, standardDictionaryId: any) {
    const url = `${this.StandardFlowUrl}/CreateNewFlow/`;
    const req: FullContextStandardFlow = {
      systemCode: '*',
      businessCode: '*',
      productCode: null,
      flowCode: codFlow,
      documentKind: documentKind,
      dictionaryKey: 'standardDictionary',
      categories: categories,
      standardDictionaryId : standardDictionaryId
    };
    return this.sxHttp.post(url, req)
      .toPromise()
      .then(response => response.json().data as ProductFlowConfig)
      .catch(this.handleError);
  }

  DeleteFlow(codSistema: string, codNegocio: string, codProduct: string, codflow: string, documentKind: string): Promise<boolean> {
    const url = `${this.configUrl}/DeleteFlow/${codSistema}/${codNegocio}/${codProduct}/${codflow}/${documentKind}`;

    return this.sxHttp.delete(url)
      .toPromise()
      .then(response => response.json().data as boolean)
      .catch(this.handleError);
  }

  DeleteOrUnsuscribeStandardFlow(codSistema: string, codNegocio: string, codProducto: string, documentKind: string, standardKey: string, codFlow: string): Promise<boolean> {
    const url = `${this.StandardFlowUrl}/DeleteFlow`;
    const req: FullContextStandardFlow = {
      systemCode: codSistema,
      businessCode: codNegocio,
      productCode: codProducto,
      flowCode: codFlow,
      dictionaryKey: standardKey,
      documentKind: documentKind,
      categories: null,
      standardDictionaryId:null
    };

    return this.sxHttp.post(url, req)
      .toPromise()
      .then(response => response.json().data as ProductFlowConfig)
      .catch(this.handleError);
  }

  // gets the setting of a specific flow
  getConfiguration(codSistema, codNegocio, codProducto, codFlow, documentKind, standardkey): Observable<ProductFlowConfig> {
    const url = `${this.configUrl}/GetConfiguration/${codSistema}/${codNegocio}/${codProducto}/${codFlow}/${documentKind}/${standardkey}`;
    return this.sxHttp.get(url)
      .map(response => response.json().data as ProductFlowConfig)
      .catch(this.handleError);
  }

  // gets the setting of a specific standard flow
  getStandardConfiguration(codSistema, codNegocio, codProducto, codFlow, documentKind, standardkey): Observable<ProductFlowConfig> {
    const url = `${this.StandardFlowUrl}/GetConfiguration/${codSistema}/${codNegocio}/${codProducto}/${codFlow}/${documentKind}/${standardkey}`;
    return this.sxHttp.get(url)
      .map(response => response.json().data as ProductFlowConfig)
      .catch(this.handleError);
  }

  // gets the setting of a specific flow
  getDesignerConfiguration(codSistema, codNegocio, codProducto, codFlow, documentKind, standardkey): Observable<ProductFlowDesignerConfig> {
    const url = `${this.configUrl}/GetDesignerConfiguration`;
    const req: {} = {
      systemCode: codSistema,
      businessCode: codNegocio,
      productCode: codProducto,
      flowCode: codFlow,
      documentKind: documentKind,
      dictionaryKey: standardkey
    };

    return this.sxHttp.post(url, req)
      .map(response => response.json().data as ProductFlowDesignerConfig)
      .catch(this.handleError);
  }

  getConfigurationById(codSistema, codNegocio, codProducto, id): Observable<ProductFlowConfig> {
    const url = `${this.configUrl}/GetConfigurationById/${codSistema}/${codNegocio}/${codProducto}/${id}`;
    return this.sxHttp.get(url)
      .map(response => response.json().data as ProductFlowConfig)
      .catch(this.handleError);
  }

  // gets a rule by complete context
  getRuleByContext(codSystem, codBusiness, codProduct, codRuleEvent, codCommand, codFlow, codPage?): Observable<Rule> {
    let url =
      `${this.configUrl}/RuleByContext/${codSystem}/${codBusiness}/${codProduct}/${codRuleEvent}/${codCommand}/${codFlow}/null`;

    if (codPage != null) {
      url += `/${codPage}`;
    }

    return this.sxHttp.get(url)
      .map(response => response.json().data as Rule)
      .catch(this.handleError);

  }
  getRuleByContextForStandard(codRuleEvent, codCommand, codFlow, standardKey, codPage?): Observable<Rule> {
    let url = `${this.StandardFlowUrl}/RuleByContext/${codRuleEvent}/${codCommand}/${codFlow}/${standardKey}`;
    if (codPage != null) {
      url += `/${codPage}`;
    }

    return this.sxHttp.get(url)
      .map(response => response.json().data as Rule)
      .catch(this.handleError);
  }

  // post and asociates a rule to a complete context
  createOrUpdateRule(req: FlowRulePostRequest): Promise<Rule> {
    const url = `${this.configUrl}/UpdateRuleByContext`;

    return this.sxHttp.post(url, req)
      .toPromise()
      .then(response => response.json().data as Rule)
      .catch(this.handleError);
  }

  UpdateLaunchEvent(launchEvent: LaunchEvent): Promise<boolean> {
    const url = `${this.configUrl}/UpdateLaunhEventByContext`;

    return this.sxHttp.post(url, launchEvent)
      .toPromise()
      .then(response => response.json().data as boolean)
      .catch(this.handleError);
  }

  getCategories(): Promise<any[]> {
    const url = `${this.CategoriesUrl}/GetForStandardFlow`;
    return this.sxHttp.get(url).toPromise()
      .then(response => {
        return response.json().data
      })
      .catch(this.handleError);
  }
  
  getStandardDictionary(): Promise<StandardDictionary[]> {
    const url = `${this.standardDictionary}/getall`;
    return this.sxHttp.get(url).toPromise()
      .then(response => {
        return response.json().data as StandardDictionary[]
      })
      .catch(this.handleError);
  }

  // deletes a rule by refering to the full context
  deleteRuleByContext(req: FlowRulePostRequest): Observable<Rule> {
    const url = `${this.configUrl}/Delete`;

    return this.sxHttp.post(url, req)
      .map(response => response.json().data as Rule)
      .catch(this.handleError);
  }

  importFlowFromFile(flowToImport: FlowImportRequest): Promise<boolean> {
    const url = `${this.configUrl}/ImportFlowFromFile`;

    return this.sxHttp.post(url, flowToImport)
      .toPromise()
      .then(response => response.json().data as boolean)
      .catch(this.handleError);
  }
  
  importStandardFlowFromFile(flowToImport: FlowImportRequest): Promise<boolean> {
    const url = `${this.StandardFlowUrl}/ImportFlowFromFile`;
    return this.sxHttp.post(url, flowToImport)
      .toPromise()
      .then(response => response.json().data as boolean)
      .catch(this.handleError);
  }

  getDynamicStyle(codSistema, codNegocio, codProducto, codFlow, documentKind, standardkey): Observable<FlowDynamicStyle> {
    const url = `${this.configUrl}/GetDynamicStyle/${codSistema}/${codNegocio}/${codProducto}/${codFlow}/${documentKind}/${standardkey}`;
    return this.sxHttp.get(url)
      .map(response => response.json().data as FlowDynamicStyle)
      .catch(this.handleError);
  }

  saveDynamicStyle(config: FlowDynamicStyle): Promise<boolean> {
    const url = `${this.configUrl}/UpdateDynamicStyle`;
    return this.sxHttp.post(url, config)
      .toPromise()
      .then(response => response.json().data as boolean)
      .catch(this.handleError);
  }

  exportStandard(codFlow,documentKind,standardKey): Observable<StandardFileContentModel> {
    const url = `${this.StandardFlowUrl}/Export/${codFlow}/${documentKind}/${standardKey}`;
    return this.sxHttp.get(url)
      .map(response => response.json().data as StandardFileContentModel)
      .catch(this.handleError);
  }

  private handleError(error: any): Promise<any> {
    console.error('Ocurrió un error', error);
    return Promise.reject(error.message || error);
  }

}



// WEBPACK FOOTER //
// D:/a/1/s/BackOffice/SOC/SxSoc.ClientWeb/src/app/infrastructure/services/productFlow.service.ts